import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as styles from './App.module.scss';
import FiltersTray from '@components/filters-tray/FiltersTray';
import { ToastMessageContextProvider } from '@components/toast-message/ToastMessage';
import {
  ClientContent,
  ClientFooter,
  useAuthentication,
  useClientSideNav,
} from '@ecosystem/newron-client-lib';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  actionCreatorsHitlistsRetailerClient,
  actionCreatorsMarketPremiumClientState,
  actionCreatorsRetailerClient,
  actionCreatorsRetailKadClient,
  actionCreatorsSellerInsightsRetailerClient,
} from '@state/Index';
import { COLLECTION_PATH, getPageTypeAndNameByPathName, NOTIFICATIONS_PREFERENCES } from '@utils/UrlUtils';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { UserPersistedFiltersProvider } from '@utils/hook/saveFilter/UserFiltersContext';
import { setLocalStorage } from '@utils/StorageUtils';
import { useResetSelectedFilters, useUpdateFiltersTrayOpenedAsExpanded } from '@utils/hook/sideFilters/UseSideFiltersTray';
import { useSubscriptions } from '@components/subscriptions/SubscriptionsProvider';
import AppRoutes from './routes/AppRoutes';
import { useInitializeSubscriptions } from '@hooks/UseInitializeSubscriptions';
import { constructAccessObject } from '@utils/page-analytics/PageAnalytics';
import packageJson from '../package.json';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { resizeChart } from '@data-visualization/utilities/ChartUtils';
import './widget.scss';

const queryClient = new QueryClient();

const App: React.FC = () => {
  const dispatch = useDispatch();
  const {
    updateRetailerClient,
    updateHitlistsRetailerClient,
    updateRetailKadClient,
    updateMarketPremiumRetailerClient,
    updateSellerInsightsRetailerClient,
  } = bindActionCreators(
    {
      ...actionCreatorsRetailerClient,
      ...actionCreatorsHitlistsRetailerClient,
      ...actionCreatorsRetailKadClient,
      ...actionCreatorsMarketPremiumClientState,
      ...actionCreatorsSellerInsightsRetailerClient,
    },
    dispatch,
  );

  const retailerState = useSelector((s: any) => s.retailerClient);
  const hitlistsState = useSelector((s: any) => s.hitlistsRetailerClient);
  const retailKadState = useSelector((s: any) => s.retailKadClient);
  const marketState = useSelector((s: any) => s.marketPremiumRetailerClient);
  const sellerState = useSelector((s: any) => s.sellerInsightsRetailerClient);

  const [loading, setLoading] = useState(true);
  const pagesConfig = usePageConfig();
  const { isAuthenticating, user } = useAuthentication();
  const { expanded } = useClientSideNav();
  const location = useLocation();

  pagesConfig.setUserId(user?.sub);

  useEffect(() => {
    const { pageType, pageName } = getPageTypeAndNameByPathName(location.pathname);
    pagesConfig.setPageType(pageType);
    pagesConfig.setPageName(pageName);
  }, [location.pathname]);

  useResetSelectedFilters();
  useUpdateFiltersTrayOpenedAsExpanded();

  const { loadingSubscriptions } = useSubscriptions();
  const {
    retailer,
    hitlists,
    retailKad,
    market,
    seller,
    hitlistsLoading,
    retailKadLoading,
    marketPremiumLoading,
    sellerInsightsLoading,
  } = useInitializeSubscriptions({ setLoading });

  useEffect(() => {
    updateClientNames({
      retailer,
      hitlists,
      retailKad,
      market,
      seller,
      states: { retailerState, hitlistsState, retailKadState, marketState, sellerState },
      updaters: {
        updateRetailerClient,
        updateHitlistsRetailerClient,
        updateRetailKadClient,
        updateMarketPremiumRetailerClient,
        updateSellerInsightsRetailerClient,
      },
    });
  }, [retailer, hitlists, retailKad, market, seller]);

  const ready =
    !loading &&
    !loadingSubscriptions &&
    !isAuthenticating &&
    !hitlistsLoading &&
    !retailKadLoading &&
    !marketPremiumLoading &&
    !sellerInsightsLoading;

  useEffect(() => {
    if (!ready) return;

    const access = constructAccessObject(
      pagesConfig.clientSubscription,
      pagesConfig.hitlistsClientSubscription,
      pagesConfig.retailKadClientSubscription,
      pagesConfig.marketPremiumClientSubscription,
      pagesConfig.sellerInsightsClientSubscription,
    );
    pagesConfig.setAccess(access);
    setLocalStorage('access', JSON.stringify(access));
  }, [ready]);

  useEffect(() => {
    resizeChart();
  }, [pagesConfig.filtersTrayOpened, expanded]);

  const isCollectionOrNotification = [COLLECTION_PATH, NOTIFICATIONS_PREFERENCES].some((prefix) =>
    location.pathname.startsWith(prefix),
  );
  return ready ? (
    <ErrorBoundary fallbackRender={() => null}>
      <ClientContent>
        <ToastMessageContextProvider>
          <QueryClientProvider client={queryClient}>
            <UserPersistedFiltersProvider>
              <div className={isCollectionOrNotification ? undefined : styles.App}>
                <AppRoutes
                  data-testid="app-routes"
                  retailerClient={retailer}
                  hitlistsRetailerClient={hitlists}
                  retailKadClient={retailKad}
                  marketPremiumRetailerClient={market}
                  sellerInsightsRetailerClient={seller}
                />
                {!isCollectionOrNotification && <ClientFooter version={`v${packageJson.version}`} />}
              </div>
            </UserPersistedFiltersProvider>
          </QueryClientProvider>
          {!isCollectionOrNotification && <FiltersTray data-testid="filters-tray" />}
        </ToastMessageContextProvider>
      </ClientContent>
    </ErrorBoundary>
  ) : null;
};

export default App;

function updateClientNames({
  retailer,
  hitlists,
  retailKad,
  market,
  seller,
  states,
  updaters,
}: {
  retailer: any[];
  hitlists: any[];
  retailKad: any[];
  market: any[];
  seller: any[];
  states: any;
  updaters: any;
}) {
  const { retailerState, hitlistsState, retailKadState, marketState, sellerState } = states;

  const {
    updateRetailerClient,
    updateHitlistsRetailerClient,
    updateRetailKadClient,
    updateMarketPremiumRetailerClient,
    updateSellerInsightsRetailerClient,
  } = updaters;

  if (retailer.length) {
    const rn =
      retailerState.clientName === ''
        ? retailer[0].clientName
        : retailer.find((c) => c.clientId == retailerState.clientId)?.clientName;

    const hn = retailer.find((c) => c.clientId === hitlistsState.clientId)?.clientName ?? '';
    updateRetailerClient({ ...retailerState, clientName: rn });
    updateHitlistsRetailerClient({ ...hitlistsState, clientName: hn });
  }
  if (retailKad.length) {
    updateRetailKadClient({ ...retailKadState, clientName: retailKad[0].clientName });
  }
  if (market.length) {
    updateMarketPremiumRetailerClient({ ...marketState, clientName: market[0].clientName });
  }
  if (seller.length) {
    updateSellerInsightsRetailerClient({ ...sellerState, clientName: seller[0].clientName });
  }
}
